import React, { useEffect, useState, useRef } from "react";
import axios from "axios";

import {
  Table,
  Container,
  Row,
  Modal,
  ModalHeader,
  Spinner,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import { PURCHASE_API } from "../../../api/api";

import { FiDownload } from "react-icons/fi";
import { FaRegEye } from "react-icons/fa";

import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import Logo from "../../../assets/Skilledition-logo.png";
import Nofound from "../../../assets/not-found.png";

import { handleApiError } from "../../../utils/apiErrorHandler";

import "./StudentPurchase.css";

const StudentPurchase = () => {
  const navigate = useNavigate();

  const [purchaseData, setPurchaseData] = useState([]);

  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [downloadInvoice, setDownloadInvoice] = useState(null);

  const invoiceModalRef = useRef(null);
  const invoicePdfRef = useRef(null);
  // If user clicks multiple times quickly:
  const [pdfLoading, setPdfLoading] = useState(false);

  //loading
  const [loadingPurchase, setLoadingPurchase] = useState(true);

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      setLoadingPurchase(false);
      navigate("/signin");

      return;
    }

    const fetchPurchase = async () => {
      try {
        const response = await axios.post(
          PURCHASE_API.GET_PURCHASE_LIST,
          { student_id: userId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = response.data;

        if (data.statusCode === 200) {
          setPurchaseData(data.data || []);
        } else {
          // This will handle any 2xx response that is not 201
          showErrorToast(data.message);
        }
      } catch (error) {
        console.error("Error purchase:", error);
        // Server responded with non-2xx status (like 400, 500)

        // CHECK (expiry / invalid token)
        handleApiError(error, navigate, showErrorToast, "/signin");
      } finally {
        setLoadingPurchase(false);
      }
    };

    fetchPurchase();
  }, [navigate]);

  const fetchInvoiceData = async (studentCourseId) => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/signin");

      return;
    }

    try {
      const response = await axios.post(
        PURCHASE_API.GET_PURCHASE_INVOICE,
        { student_course_id: studentCourseId },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        return data.data;
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error fetching invoice:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/signin");
    }
  };

  const handlePurchaseView = async (studentCourseId) => {
    const invoiceData = await fetchInvoiceData(studentCourseId);

    if (invoiceData) {
      setSelectedInvoice(invoiceData);
      setIsInvoiceModalOpen(true);
    }
  };

  // const handlePurchaseDownload = async (studentCourseId) => {
  //   const invoiceData = await fetchInvoiceData(studentCourseId);
  //   if (!invoiceData) return;

  //   setDownloadInvoice(invoiceData);

  //   setTimeout(async () => {
  //     if (!invoicePdfRef.current) return;

  //     const canvas = await html2canvas(invoicePdfRef.current, {
  //       scale: 2,
  //       useCORS: true,
  //       backgroundColor: "#ffffff",
  //     });

  //     const imgData = canvas.toDataURL("image/png");
  //     const pdf = new jsPDF("p", "mm", "a4");

  //     const imgWidth = 210;
  //     const imgHeight = (canvas.height * imgWidth) / canvas.width;

  //     pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
  //     pdf.save(`Invoice-${invoiceData.order_id}.pdf`);

  //     setDownloadInvoice(null);
  //   }, 200);
  // };

  const handlePurchaseDownload = async (studentCourseId) => {
    if (pdfLoading) return;

    setPdfLoading(true);

    const invoiceData = await fetchInvoiceData(studentCourseId);
    if (!invoiceData) {
      setPdfLoading(false);
      return;
    }

    setDownloadInvoice(invoiceData);
  };

  useEffect(() => {
    const generatePDF = async () => {
      if (!downloadInvoice || !invoicePdfRef.current) return;

      // wait one frame (important for heavy DOM)
      await new Promise(requestAnimationFrame);

      const canvas = await html2canvas(invoicePdfRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");

      const imgWidth = 210;
      const pageHeight = 297;

      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);

      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`Invoice-${downloadInvoice.invoice_number || downloadInvoice.order_id}.pdf`);

      setPdfLoading(false);
      setDownloadInvoice(null);
    };

    generatePDF();
  }, [downloadInvoice]);

  if (loadingPurchase) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Container fluid className="student-certificate-section-container">
      <Row className="align-items-center mb-3">
        <h2 className="student-certificate-section-title">My Purchase</h2>
        <p className="student-certificate-section-description">
         Access your purchased courses and continue learning. 
        </p>
      </Row>

      {/* student invoice data  */}
      <div className="student-purshace-section-container">
        <Table className="student-purshace-section-table">
          <thead className="student-purshace-section-thead">
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Order ID</th>
              <th>
                <div className="student-dashboard-sort-header">
                  Transaction ID
                </div>
              </th>
              <th>
                <div className="student-dashboard-sort-header">Invoice ID</div>
              </th>
              <th>Course</th>
              <th>Price</th>
              <th>
                <div className="student-dashboard-sort-header">
                  Payment Type
                </div>
              </th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {purchaseData.length > 0 ? (
              purchaseData.map((item) => (
                <tr
                  key={item.student_course_id}
                  className="student-purshace-section-row"
                >
                  <td title={item.purchase_date}>
                    {item.purchase_date || "-"}
                  </td>
                  <td title={item.type}>{item.type || "-"}</td>
                  <td title={item.order_id}>{item.order_id || "-"}</td>
                  <td title={item.transaction_id}>
                    {item.transaction_id || "-"}
                  </td>
                  <td title={item.invoice_number}>
                    {item.invoice_number || "-"}
                  </td>
                  <td title={item.course_title}>{item.course_title || "-"}</td>
                  <td title={item.order_amount}>{item.order_amount || "-"}</td>
                  <td title={item.payment_method}>
                    {item.payment_method || "-"}
                  </td>
                  <td>
                    <span
                      className={
                        item.status?.toLowerCase() === "success"
                          ? "student-purshace-section-badge-success"
                          : item.status?.toLowerCase() === "failed"
                            ? "student-purshace-section-badge-failed"
                            : "student-purshace-section-badge-pending"
                      }
                    >
                      {item.status}
                    </span>
                  </td>
                  <td className="student-purshace-section-actions">
                    <FaRegEye
                      className="student-purshace-section-icon"
                      onClick={() => handlePurchaseView(item.student_course_id)}
                    />
                    <FiDownload
                      className={`student-purshace-section-icon ${
                        item.status?.toLowerCase() !== "success"
                          ? "student-purshace-disabled-icon"
                          : ""
                      }`}
                      title={
                        item.status?.toLowerCase() === "success"
                          ? "Download Invoice"
                          : "Invoice available only for successful payments"
                      }
                      onClick={() => {
                        if (item.status?.toLowerCase() === "success") {
                          handlePurchaseDownload(item.student_course_id);
                        }
                      }}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="text-center py-4">
                  <img
                    src={Nofound}
                    alt="No Data Found"
                    className="student-dash-purshace-empty-img"
                  />
                  <p className="student-dash-purshace-no-data">
                    No Purchase Data Found
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Hidden Invoice For PDF */}
      {downloadInvoice && (
        <div
          style={{
            position: "fixed",
            top: "-10000px",
            left: "-10000px",
            background: "#ffffff",
          }}
        >
          <div
            ref={invoicePdfRef}
            className="student-purshace-invoice-wrapper student-purshace-pdf-download-mode"
          >
            <div className="student-purshace-invoice-top">
              <div className="student-purshace-invoice-left">
                <div className="student-purshace-invoice-logo">
                  <img
                    src={Logo}
                    alt="skilledition"
                    className="student-purshace-logo-img"
                  />
                </div>
                <p className="student-purshace-invoice-powered">
                  Powered by Digisphere
                </p>

                <p className="student-purshace-mt-20">
                  Ramavarappadu,
                  <br />
                  Vijayawada,
                  <br />
                  Andhra Pradesh, 520010
                  <br />
                  Telephone: (+91) 814-387-9627
                </p>

                <p className="student-purshace-invoice-to">
                  <strong>Invoice To</strong>
                  <br />
                  {downloadInvoice.full_name || "-"}
                </p>
              </div>

              <div className="student-purshace-invoice-download-right">
                <p className="student-purshace-invoice-number">
                  Invoice ID - {downloadInvoice.invoice_number || "-"}
                </p>

                <p className="student-purshace-invoice-number">
                  Order ID - {downloadInvoice.order_id || "-"}
                </p>
                <p className="student-purshace-invoice-number">
                  Transaction ID - {downloadInvoice.transaction_id || "-"}
                </p>

                <p className="student-dash-purshace-date">
                  Date: {downloadInvoice.purchase_date || "-"}
                </p>

                <div className="student-purshace-payment-details">
                  <h6>Payment Details:</h6>

                  <p>Payment Type : {downloadInvoice.payment_method || "-"}</p>

                  <p>
                    Payment Status :
                    <span
                      className={`ms-2 ${
                        downloadInvoice.status?.toLowerCase() === "success"
                          ? "student-purshace-section-badge-success"
                          : downloadInvoice.status?.toLowerCase() === "failed"
                            ? "student-purshace-section-badge-failed"
                            : "student-purshace-section-badge-pending"
                      }`}
                    >
                      {downloadInvoice.status || "-"}{" "}
                    </span>
                  </p>
                  <p>Email : {downloadInvoice.email || "-"}</p>
                  <p>Phone : {downloadInvoice.phone_number || "-"}</p>
                  {/* <p>Country : India</p> */}
                  <p>Address : {downloadInvoice.address || "-"}</p>
                  <p>Pincode : {downloadInvoice.pincode || "-"}</p>
                </div>
              </div>
            </div>

            <div className="student-purshace-invoice-table-wrapper">
              <table className="student-purshace-invoice-table">
                <thead>
                  <tr>
                    <th>Course</th>
                    <th>Quantity</th>
                    <th>Price</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td> {downloadInvoice.course_title || "-"}</td>
                    <td>01</td>
                    <td>₹ {downloadInvoice.order_amount || 0}</td>
                    <td className="student-purshace-text-blue">
                      ₹ {downloadInvoice.order_amount || 0}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="student-purshace-total-section">
              <div className="student-purshace-total-row">
                <span>Sub-Total Amount</span>
                <span>₹ {downloadInvoice.order_amount || 0}</span>
              </div>

              {/* <div className="student-purshace-total-row">
                <span>GST (18%)</span>
                <span>
                  ₹{(Number(downloadInvoice.amount) * 0.18).toFixed(2)}
                </span>
              </div> */}

              <div className="student-purshace-total-row student-purshace-grand-total">
                <span>Grand Total</span>
                <span>
                  {/* ₹{(Number(downloadInvoice.order_amount) * 1.18).toFixed(2)} */}
                  ₹ {Number(downloadInvoice.order_amount || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* modal data view  */}
      <Modal
        show={isInvoiceModalOpen}
        onHide={() => setIsInvoiceModalOpen(false)}
        size="xl"
        centered
        className="student-purshace-modal"
        backdrop="static"
        keyboard={false}
      >
        <ModalHeader closeButton className="student-purshace-modal-header">
          <h6 className="student-purshace-modal-title">Invoice Info</h6>
        </ModalHeader>

        <Modal.Body className="p-0">
          {selectedInvoice && (
            <div
              ref={invoiceModalRef}
              className="student-purshace-invoice-wrapper"
            >
              <div className="student-purshace-invoice-top">
                <div className="student-purshace-invoice-left">
                  <div className="student-purshace-invoice-logo">
                    <img
                      src={Logo}
                      alt="skilledition"
                      className="student-purshace-logo-img"
                    />
                  </div>
                  <p className="student-purshace-invoice-powered">
                    Powered by Digisphere
                  </p>

                  <p className="student-purshace-mt-20">
                    Ramavarappadu,
                    <br />
                    Vijayawada,
                    <br />
                    Andhra Pradesh, 520010
                    <br />
                    Telephone: (+91) 814-387-9627
                  </p>

                  <p className="student-purshace-invoice-to">
                    <strong>Invoice To</strong>
                    <br />
                    {selectedInvoice.full_name || "-"}
                  </p>
                </div>

                <div className="student-purshace-invoice-right">
                  <p className="student-purshace-invoice-number">
                    Invoice ID - {selectedInvoice.invoice_number || "-"}
                  </p>

                  <p className="student-purshace-invoice-number">
                    Order ID - {selectedInvoice.order_id || "-"}
                  </p>
                  <p className="student-purshace-invoice-number">
                    Transaction ID - {selectedInvoice.transaction_id || "-"}
                  </p>

                  <p className="student-dash-purshace-date">
                    Date: {selectedInvoice.purchase_date || "-"}
                  </p>

                  <div className="student-purshace-payment-details">
                    <h6>Payment Details:</h6>
                    <p>
                      Payment Type : {selectedInvoice.payment_method || "-"}
                    </p>
                    <p>
                      Payment Status :
                      <span
                        className={`ms-2 ${
                          selectedInvoice.status?.toLowerCase() === "success"
                            ? "student-purshace-section-badge-success"
                            : selectedInvoice.status?.toLowerCase() === "failed"
                              ? "student-purshace-section-badge-failed"
                              : "student-purshace-section-badge-pending"
                        }`}
                      >
                        {selectedInvoice.status || "-"}
                      </span>
                    </p>
                    {/* <p>Country : India</p>
                    <p>City : Vijayawada</p> */}
                    <p>Email : {selectedInvoice.email || "-"}</p>
                    <p>Phone : {selectedInvoice.phone_number || "-"}</p>
                    <p>Address : {selectedInvoice.address || "-"}</p>
                    <p>Pincode : {selectedInvoice.pincode || "-"}</p>
                  </div>
                </div>
              </div>

              <div className="student-purshace-invoice-table-wrapper">
                <table className="student-purshace-invoice-table">
                  <thead>
                    <tr>
                      <th>Course</th>
                      <th>Quantity</th>
                      <th>Price</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td> {selectedInvoice.course_title || "-"}</td>
                      <td>01</td>
                      <td> ₹ {selectedInvoice.order_amount || 0}</td>
                      <td className="student-purshace-text-blue">
                        ₹ {selectedInvoice.order_amount || 0}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="student-purshace-total-section">
                <div className="student-purshace-total-row">
                  <span>Sub-Total Amount</span>
                  <span>₹ {selectedInvoice.order_amount || 0}</span>
                </div>

                {/* <div className="student-purshace-total-row">
                  <span>GST (18%)</span>
                  <span>
                    ₹{(Number(selectedInvoice.amount) * 0.18).toFixed(2)}
                  </span>
                </div> */}

                <div className="student-purshace-total-row student-purshace-grand-total">
                  <span>Grand Total</span>
                  <span>₹ {selectedInvoice.order_amount || 0}</span>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default StudentPurchase;
